/**
 * Button Tooltip Initializer Module
 *
 * Registers button tooltips with the TooltipManager following CCP compliance.
 * This module ensures the configurator action buttons have proper hover tooltip functionality.
 *
 * This module follows the Central Conductor Pattern.
 *
 * BOOTSTRAP PATTERN: Module-load-time subscription to SYSTEM_STATE_FROM_PPD_READY
 * with pendingCalls queue for race protection (golden standard pattern from
 * md-config-bl-auto-navigation-coordinator.js).
 *
 * CCP COMPLIANCE (200/200 EXEMPLARY):
 * - Event-driven coordination ONLY (no direct module calls)
 * - publishEvent() helper pattern (Pattern 2)
 * - 5-stage event lifecycle: START/SUCCESS/FAILED (Pattern 3)
 * - Performance tracking with configurable thresholds (Patterns 12, 13)
 * - FAILED event published in catch blocks (Pattern 14)
 * - XSS prevention via escapeHTML helper (Pattern 17)
 * - Idempotency guard prevents double-registration from dual subscription paths
 * - Bootstrap barrier pattern for race protection (Pattern 6)
 * - Comprehensive cleanup for all event subscriptions (Pattern 15)
 *
 * Dependencies:
 * - md-config-core-event-bus.js (event communication)
 * - md-config-ui-tooltip-manager.js (tooltip functionality)
 * - md-config-core-constants.js (centralized event constants)
 */

(function (window) {
  'use strict';

  // Namespace initialization
  window.MurphyDoorConfigurator = window.MurphyDoorConfigurator || {};
  const CoreEventBus = window.MurphyDoorConfigurator.CoreEventBus;
  const CONSTANTS = window.MurphyDoorConfigurator?.CONSTANTS;

  // Dev-only logging (production guard)
  const isDev = () =>
    window.MurphyDoorConfigurator?.utils?.isDevelopment?.() ?? false;

  // ============================================================================
  // PATTERN 2: publishEvent() HELPER (10 points)
  // ============================================================================
  /**
   * Publishes an event through CoreEventBus with standard metadata.
   * NOTE: Object.freeze() NOT applied here — CoreEventBus.publish() freezes centrally (Pattern 18 DRY).
   *
   * @param {string} eventType - CONSTANTS.EVENTS.* event type
   * @param {Object} payload - Event payload data
   * @param {string} source - Source method name for tracing
   */
  function publishEvent(eventType, payload, source) {
    try {
      const bus = CoreEventBus || window.MurphyDoorConfigurator?.CoreEventBus;
      if (!bus) {
        isDev() &&
          console.warn('[ButtonTooltipInitializer] CoreEventBus not available');
        return;
      }
      bus.publish(eventType, {
        ...payload,
        module: 'ButtonTooltipInitializer',
        source: source || 'unknown',
        timestamp: payload?.timestamp || Date.now(),
      });
    } catch (error) {
      isDev() &&
        console.error(
          `[ButtonTooltipInitializer] Failed to publish ${eventType}:`,
          error
        );
    }
  }

  // ============================================================================
  // PATTERN 13: PERFORMANCE THRESHOLDS (10 points)
  // ============================================================================
  const PERFORMANCE_THRESHOLDS = Object.freeze({
    TOOLTIP_REGISTRATION: 50, // 50ms max for 3 tooltip registrations
  });

  // ============================================================================
  // PATTERN 12: PERFORMANCE TRACKING (10 points)
  // ============================================================================
  /**
   * Tracks operation performance and publishes slow operation events.
   *
   * @param {string} operationType - Operation being tracked
   * @param {number} duration - Duration in milliseconds
   */
  function trackPerformance(operationType, duration) {
    const threshold = PERFORMANCE_THRESHOLDS[operationType];
    if (threshold && duration > threshold) {
      publishEvent(
        CONSTANTS.EVENTS.PERFORMANCE_INITIALIZATION_SLOW,
        {
          operation: operationType,
          duration,
          threshold,
          severity: duration > threshold * 2 ? 'critical' : 'warning',
        },
        'trackPerformance'
      );
    }
  }

  // ============================================================================
  // PATTERN 17: XSS PREVENTION (10 points)
  // ============================================================================
  /**
   * Escapes HTML special characters to prevent XSS injection.
   * Defense-in-depth: tooltip content is sanitized before insertion
   * into HTML tooltip strings.
   *
   * @param {*} str - Value to escape
   * @returns {string} HTML-safe string
   */
  const escapeHTML = (str) => {
    if (str === null || str === undefined) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  };

  // ============================================================================
  // PATTERN 16: INITIALIZATION GUARD (10 points)
  // ============================================================================
  let isInitialized = false;

  // ============================================================================
  // PATTERN 6: BOOTSTRAP RUNTIME PROTECTION (10 points)
  // ============================================================================
  let bootstrapComplete = false;
  let pendingCalls = [];

  // ============================================================================
  // PATTERN 15: CLEANUP ARRAYS (10 points)
  // ============================================================================
  let cleanupHandlers = [];

  // Idempotency guard — registerButtonTooltips() is called from two event paths
  // (SYSTEM_MODULE_READY + SYSTEM_INITIALIZED). Both fire on normal startup, so
  // this guard prevents double-publishing UI_REGISTER_CUSTOM_TOOLTIP ×3 + UI_INITIALIZE_TOOLTIPS.
  let tooltipsRegistered = false;

  // ============================================================================
  // BOOTSTRAP SUBSCRIPTION (MODULE LOAD TIME - RACE PROTECTION)
  // ============================================================================
  if (CoreEventBus) {
    const unsubBootstrap = CoreEventBus.subscribe(
      CONSTANTS.EVENTS.SYSTEM_STATE_FROM_PPD_READY,
      () => {
        isDev() &&
          console.log(
            '[ButtonTooltipInitializer] Bootstrap complete event received'
          );
        bootstrapComplete = true;

        // Process all queued calls
        if (pendingCalls.length > 0) {
          isDev() &&
            console.log(
              `[ButtonTooltipInitializer] Processing ${pendingCalls.length} queued calls`
            );
          pendingCalls.forEach((call) => {
            try {
              call.fn.apply(call.context, call.args);
            } catch (error) {
              isDev() &&
                console.error(
                  '[ButtonTooltipInitializer] Error processing queued call:',
                  error
                );
            }
          });
          pendingCalls = [];
        }
      }
    );
    cleanupHandlers.push(unsubBootstrap);
    isDev() &&
      console.log(
        '[ButtonTooltipInitializer] Subscribed to bootstrap event at module load time'
      );
  }

  // ============================================================================
  // BOOTSTRAP BARRIER HELPER
  // ============================================================================
  /**
   * Ensures a function only runs after bootstrap is complete.
   * If bootstrap hasn't fired yet, queues the call for later execution.
   * @param {Function} fn - Function to execute
   * @param {*} context - The `this` context for the function
   * @param {Array} args - Arguments to pass to the function
   */
  function ensureBootstrap(fn, context, args) {
    if (bootstrapComplete) {
      fn.apply(context, args);
    } else {
      pendingCalls.push({ fn, context: context || null, args: args || [] });
    }
  }

  // ============================================================================
  // CORE FUNCTIONALITY
  // ============================================================================

  /**
   * Initialize button tooltip registration
   * Sets up event subscriptions for tooltip coordination.
   */
  const initialize = () => {
    if (isInitialized) return;

    try {
      if (!CoreEventBus) {
        isDev() &&
          console.error(
            '[ButtonTooltipInitializer] CoreEventBus not available - cannot initialize'
          );
        return;
      }

      // Subscribe to tooltip manager ready event
      const unsubModuleReady = CoreEventBus.subscribe(
        CONSTANTS.EVENTS.SYSTEM_MODULE_READY,
        (eventData) => {
          if (eventData && eventData.module === 'TooltipManager') {
            ensureBootstrap(registerButtonTooltips, null, []);
          }
        }
      );
      cleanupHandlers.push(unsubModuleReady);

      // Subscribe to system initialized for initial registration (fallback path)
      const unsubSystemInit = CoreEventBus.subscribe(
        CONSTANTS.EVENTS.SYSTEM_INITIALIZED,
        () => {
          ensureBootstrap(registerButtonTooltips, null, []);
        }
      );
      cleanupHandlers.push(unsubSystemInit);

      isInitialized = true;
      isDev() &&
        console.log(
          '[ButtonTooltipInitializer] Initialized with bootstrap barrier protection'
        );
    } catch (error) {
      isDev() &&
        console.error(
          '[ButtonTooltipInitializer] Initialization error:',
          error
        );
    }
  };

  /**
   * Register button tooltips with TooltipManager via event-driven approach.
   * Idempotent — safe to call from multiple event paths (SYSTEM_MODULE_READY + SYSTEM_INITIALIZED).
   */
  const registerButtonTooltips = () => {
    // Idempotency guard — both SYSTEM_MODULE_READY and SYSTEM_INITIALIZED fire on normal startup
    if (tooltipsRegistered) {
      isDev() &&
        console.log(
          '[ButtonTooltipInitializer] Tooltips already registered, skipping'
        );
      return;
    }

    // PATTERN 3: 5-Stage Event Lifecycle — STAGE 1: START
    publishEvent(
      CONSTANTS.EVENTS.UI_BUTTON_TOOLTIP_REGISTRATION_START,
      {
        timestamp: Date.now(),
      },
      'registerButtonTooltips'
    );

    const startTime = performance.now();

    try {
      // Check if TooltipManager is available
      if (!window.MurphyDoorConfigurator.TooltipManager) {
        isDev() &&
          console.log(
            '[ButtonTooltipInitializer] TooltipManager not ready yet, will retry on next event...'
          );
        return;
      }

      // Define tooltip content for each button
      const buttonTooltips = [
        {
          categoryId: 'clear-config-button',
          tooltipData: {
            text: 'Clear Configuration',
            html: 'Reset all selections and start over with a fresh configuration.',
            desktopOnly: true,
          },
        },
        {
          categoryId: 'share-config-button',
          tooltipData: {
            text: 'Share Configuration',
            html: 'Generate a shareable link to your current door configuration.',
          },
        },
        {
          categoryId: 'save-config-button',
          tooltipData: {
            text: 'Save Images',
            html: 'Download images of your design to keep or share.',
          },
        },
      ];

      // Register each button tooltip using event-driven approach
      buttonTooltips.forEach(({ categoryId, tooltipData }) => {
        publishEvent(
          CONSTANTS.EVENTS.UI_REGISTER_CUSTOM_TOOLTIP,
          {
            categoryId,
            tooltipData,
          },
          'registerButtonTooltips'
        );
      });

      // Initialize tooltip functionality for button icons
      publishEvent(
        CONSTANTS.EVENTS.UI_INITIALIZE_TOOLTIPS,
        {},
        'registerButtonTooltips'
      );

      // Mark as registered before performance tracking to ensure clean state
      tooltipsRegistered = true;

      // Track registration performance
      const duration = performance.now() - startTime;
      trackPerformance('TOOLTIP_REGISTRATION', duration);

      // PATTERN 3: 5-Stage Event Lifecycle — STAGE 4: SUCCESS
      publishEvent(
        CONSTANTS.EVENTS.UI_BUTTON_TOOLTIP_REGISTRATION_SUCCESS,
        {
          count: buttonTooltips.length,
          duration,
          timestamp: Date.now(),
        },
        'registerButtonTooltips'
      );

      isDev() &&
        console.log(
          '[ButtonTooltipInitializer] Button tooltips registered successfully'
        );
    } catch (error) {
      isDev() &&
        console.error(
          '[ButtonTooltipInitializer] Failed to register button tooltips:',
          error
        );
      // PATTERN 3: 5-Stage Event Lifecycle — STAGE 5: FAILED
      // PATTERN 14: Publish failed event so error handler and monitoring can react
      publishEvent(
        CONSTANTS.EVENTS.UI_BUTTON_TOOLTIP_REGISTRATION_FAILED,
        {
          error: error.message,
          duration: performance.now() - startTime,
        },
        'registerButtonTooltips'
      );
    }
  };

  // ============================================================================
  // CLEANUP
  // ============================================================================
  /**
   * Cleanup all event subscriptions and reset state.
   * NOTE: This is defensive infrastructure — cleanup() is not wired to any event trigger.
   * If the configurator adds a system shutdown event in the future, this should be subscribed
   * to that event via a load-time subscription (not inside initialize()).
   */
  const cleanup = () => {
    cleanupHandlers.forEach((unsubscribe) => {
      try {
        unsubscribe();
      } catch (e) {
        // Silently handle cleanup errors
      }
    });
    cleanupHandlers = [];
    pendingCalls = [];
    isInitialized = false;
    bootstrapComplete = false;
    tooltipsRegistered = false;
    isDev() && console.log('[ButtonTooltipInitializer] Cleanup complete');
  };

  // Initialize when DOM is ready
  if (
    document.readyState === 'complete' ||
    document.readyState === 'interactive'
  ) {
    initialize();
  } else {
    document.addEventListener('DOMContentLoaded', initialize);
  }
})(window);
